// Shared constants and utilities for Expense components

export interface ApprovedItemRequest {
  documentId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  item_estimate?: {
    item: string;
    totalCost: number;
  };
  aproval_request?: {
    title: string;
  };
}

export interface ExpenseItem {
  id: string;
  title: string;
  expenseItems: any[];
  totalAmount: number;
  isExceptionRequest: boolean;
  sendExceptionApprovalTo?: string;
  approvedItem?: ApprovedItemRequest;
  attachments?: any[];
  createdAt: string;
  remianingAmount?: number;
  comment?: string;
  documentId?: string;
}

// Column definitions
export const SEC1_COLUMNS: { key: string; label: string; sortable?: boolean }[] = [
  { key: 'category', label: 'Category', sortable: true },
  { key: 'count', label: 'Count', sortable: true },
  { key: 'approvedBudget', label: 'Approved Budget', sortable: true },
  { key: 'spent', label: 'Total Spent', sortable: true },
  { key: 'remaining', label: 'Remaining', sortable: true },
];

export const SEC2_COLUMNS: { key: string; label: string; sortable?: boolean }[] = [
  { key: 'slno', label: '#' },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'date', label: 'Date', sortable: true },
  { key: 'approvedBudget', label: 'Approved Budget', sortable: true },
  { key: 'spent', label: 'Spent', sortable: true },
  { key: 'remaining', label: 'Remaining', sortable: true },
  { key: 'exception', label: 'Exception' },
  { key: 'itemsList', label: 'Items List' },
  { key: 'items', label: 'Item Count', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'status', label: 'Approval Status' },
  { key: 'link', label: 'Detail Link' },
];

// Helper functions
export const getExpenseTotalAmount = (expense: any): number => {
  if (expense.totalAmount !== null && expense.totalAmount !== undefined) {
    return expense.totalAmount;
  }
  if (expense.totalPrice !== null && expense.totalPrice !== undefined) {
    return expense.totalPrice;
  }
  return 0;
};

/**
 * Download spreadsheet for "By Category" tab — mirrors the grouped HTML table
 * with alternating group colors, borders, and a totals row.
 */
export const downloadByCategorySpreadsheet = (submittedExpenses: ExpenseItem[]) => {
  try {
    const XLSX = require('xlsx-js-style');
    const wb = XLSX.utils.book_new();

    // ── Color palettes per group (matching the UI component) ──
    const groupPalettes = [
      { headerBg: 'D6E4F0', itemBg: 'E3F2FD', border: '1976D2' }, // Blue
      { headerBg: 'D5ECD5', itemBg: 'E8F5E9', border: '2E7D32' }, // Green
      { headerBg: 'E8D5F0', itemBg: 'F3E5F5', border: '7B1FA2' }, // Purple
      { headerBg: 'FDDCB5', itemBg: 'FFF3E0', border: 'E65100' }, // Orange
      { headerBg: 'C5ECF0', itemBg: 'E0F7FA', border: '00838F' }, // Teal
    ];

    // ── Group expenses by category ──
    const groupMap: Record<string, ExpenseItem[]> = {};
    submittedExpenses.forEach(expense => {
      const ai = (expense as any).ApprovedItem || (expense as any).approvedItem || {};
      const ar = ai.aproval_request || {};
      const ie = ai.item_estimate || {};
      const category = ie.item || ar.title || (expense.isExceptionRequest ? 'Exception' : 'Uncategorized');
      if (!groupMap[category]) groupMap[category] = [];
      groupMap[category].push(expense);
    });

    const groups = Object.entries(groupMap).map(([category, expenses]) => {
      const totalSpent = expenses.reduce((sum, e) => sum + getExpenseTotalAmount(e), 0);
      const firstAI = (expenses[0] as any).ApprovedItem || {};
      return {
        category,
        expenses,
        totalSpent,
        totalApproved: firstAI.totalPrice || 0,
        totalRemaining: firstAI.remianingAmount ?? firstAI.remainingAmount ?? 0,
        count: expenses.length,
      };
    }).sort((a, b) => a.category.localeCompare(b.category));

    // ── Helper to build border style ──
    const solidBorder = (color: string) => ({ style: 'thin' as const, color: { rgb: color } });
    const dottedBorder = (color: string) => ({ style: 'dotted' as const, color: { rgb: color } });

    // ── Build worksheet ──
    const COL_COUNT = 8;
    const ws: any = {};
    const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];
    let row = 0;

    // Header row
    const headers = ['Category', 'Title', 'Date', 'Items', 'Approved Budget (₹)', 'Spent (₹)', 'Remaining (₹)', 'Exception Status'];
    headers.forEach((h, c) => {
      ws[XLSX.utils.encode_cell({ r: row, c })] = {
        v: h, t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          fill: { fgColor: { rgb: '333333' } },
          alignment: { horizontal: c >= 4 ? 'right' : 'left', vertical: 'center' },
          border: { top: solidBorder('333333'), bottom: solidBorder('333333'), left: solidBorder('333333'), right: solidBorder('333333') },
        },
      };
    });
    row++;

    // Grand totals accumulators
    let grandApproved = 0, grandSpent = 0, grandRemaining = 0, grandCount = 0;

    groups.forEach((group, groupIdx) => {
      const palette = groupPalettes[groupIdx % groupPalettes.length];
      const bc = palette.border;
      grandApproved += group.totalApproved;
      grandSpent += group.totalSpent;
      grandRemaining += group.totalRemaining;
      grandCount += group.count;

      // ── Category header row (merged, solid borders all around) ──
      const catLabel = `${group.category}  —  ${group.count} expense${group.count !== 1 ? 's' : ''}  |  Approved: ₹${group.totalApproved.toLocaleString('en-IN')}  |  Spent: ₹${group.totalSpent.toLocaleString('en-IN')}  |  Remaining: ₹${group.totalRemaining.toLocaleString('en-IN')}`;
      for (let c = 0; c < COL_COUNT; c++) {
        ws[XLSX.utils.encode_cell({ r: row, c })] = {
          v: c === 0 ? catLabel : '', t: 's',
          s: {
            font: { bold: true, sz: 11, color: { rgb: '000000' } },
            fill: { fgColor: { rgb: palette.headerBg } },
            alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
            border: {
              top: solidBorder(bc),
              bottom: solidBorder(bc),
              left: c === 0 ? solidBorder(bc) : undefined,
              right: c === COL_COUNT - 1 ? solidBorder(bc) : undefined,
            },
          },
        };
      }
      merges.push({ s: { r: row, c: 0 }, e: { r: row, c: COL_COUNT - 1 } });
      row++;

      // ── Nested expense rows ──
      group.expenses.forEach((expense, expIdx) => {
        const ai = (expense as any).ApprovedItem || {};
        const spent = getExpenseTotalAmount(expense);
        const approvedBudget = ai.totalPrice || 0;
        const remaining = ai.remianingAmount ?? ai.remainingAmount ?? 0;
        const expenseDate = expense.createdAt
          ? new Date(expense.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';
        const itemsList = (expense.expenseItems || []).map((it: any) => it.item || '').filter(Boolean).join(', ') || 'No items';
        const exceptionStatus = expense.isExceptionRequest
          ? ((expense as any).ExceptionApproval?.status || 'Pending')
          : 'N/A';

        const isFirst = expIdx === 0;
        const isLast = expIdx === group.expenses.length - 1;
        const topBorderStyle = isFirst ? undefined : dottedBorder(bc);

        const values = [
          { v: '', t: 's' as const },
          { v: expense.title || '(Untitled)', t: 's' as const },
          { v: expenseDate, t: 's' as const },
          { v: itemsList, t: 's' as const },
          { v: approvedBudget, t: 'n' as const },
          { v: spent, t: 'n' as const },
          { v: remaining, t: 'n' as const },
          { v: exceptionStatus, t: 's' as const },
        ];

        values.forEach((cell, c) => {
          ws[XLSX.utils.encode_cell({ r: row, c })] = {
            v: cell.v, t: cell.t,
            s: {
              font: { sz: 10, color: { rgb: '333333' } },
              fill: { fgColor: { rgb: palette.itemBg } },
              alignment: { horizontal: c >= 4 && c <= 6 ? 'right' : 'left', vertical: 'center', wrapText: c === 3 },
              border: {
                top: topBorderStyle,
                bottom: isLast ? solidBorder(bc) : undefined,
                left: c === 0 ? solidBorder(bc) : undefined,
                right: c === COL_COUNT - 1 ? solidBorder(bc) : undefined,
              },
              numFmt: cell.t === 'n' ? '#,##0.00' : undefined,
            },
          };
          if (cell.t === 'n') {
            ws[XLSX.utils.encode_cell({ r: row, c })].z = '#,##0.00';
          }
        });
        row++;
      });
    });

    // ── Total row ──
    const totalValues = [
      { v: '', t: 's' as const },
      { v: 'TOTAL', t: 's' as const },
      { v: '', t: 's' as const },
      { v: '', t: 's' as const },
      { v: grandApproved, t: 'n' as const },
      { v: grandSpent, t: 'n' as const },
      { v: grandRemaining, t: 'n' as const },
      { v: '', t: 's' as const },
    ];
    totalValues.forEach((cell, c) => {
      ws[XLSX.utils.encode_cell({ r: row, c })] = {
        v: cell.v, t: cell.t,
        s: {
          font: { bold: true, sz: 11, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'D9D9D9' } },
          alignment: { horizontal: c >= 4 && c <= 6 ? 'right' : 'left', vertical: 'center' },
          border: {
            top: { style: 'medium' as const, color: { rgb: '333333' } },
            bottom: { style: 'medium' as const, color: { rgb: '333333' } },
            left: c === 0 ? { style: 'medium' as const, color: { rgb: '333333' } } : undefined,
            right: c === COL_COUNT - 1 ? { style: 'medium' as const, color: { rgb: '333333' } } : undefined,
          },
          numFmt: cell.t === 'n' ? '#,##0.00' : undefined,
        },
      };
      if (cell.t === 'n') {
        ws[XLSX.utils.encode_cell({ r: row, c })].z = '#,##0.00';
      }
    });
    row++;

    // ── Set worksheet range ──
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row - 1, c: COL_COUNT - 1 } });
    ws['!merges'] = merges;
    ws['!cols'] = [
      { wch: 22 }, { wch: 30 }, { wch: 14 }, { wch: 40 },
      { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Expenses By Category');

    const timestamp = new Date().toLocaleString('en-IN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).replace(/[/:,\s]/g, '-');
    XLSX.writeFile(wb, `Expenses_ByCategory_${timestamp}.xlsx`);
  } catch (err) {
    console.error('Error downloading By Category spreadsheet:', err);
    alert('Failed to download spreadsheet. Please try again.');
  }
};

export const downloadExpensesSpreadsheet = (submittedExpenses: ExpenseItem[]) => {
  try {
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryRows = submittedExpenses.map((expense, idx) => {
      const ai = (expense as any).ApprovedItem || (expense as any).approvedItem || {};
      const ie = ai.item_estimate || {};
      const ar = ai.aproval_request || {};
      const spent = getExpenseTotalAmount(expense);
      const exStatus = (expense as any).ExceptionApproval?.status || '';
      const approvedBudget =
        expense.isExceptionRequest && exStatus === 'Approved'
          ? spent
          : expense.isExceptionRequest && exStatus === 'Rejected'
          ? 0
          : ai.totalPrice || 0;
      const remaining = ai.remianingAmount ?? ai.remainingAmount ?? 0;
      return {
        'Sl No': idx + 1,
        'Title': expense.title || '',
        'Date': expense.createdAt
          ? new Date(expense.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : '',
        'Approval Category': ar.title || ie.item || '',
        'Approved Budget (₹)': approvedBudget,
        'Spent (₹)': spent,
        'Remaining (₹)': remaining,
        'No. of Items': expense.expenseItems?.length || 0,
        'Items List': (expense.expenseItems || []).map((it: any) => it.item || '').filter(Boolean).join(', '),
        'Exception Request': expense.isExceptionRequest ? 'Yes' : 'No',
        'Exception Approval Status': exStatus || (expense.isExceptionRequest ? 'Pending' : ''),
        'Budget Level': (expense as any).budgetWithinApprovalLevel || '',
        'Detail Link': `${window.location.origin}/expense/${(expense as any).documentId || expense.id}`,
      };
    });
    const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Expenses Summary');

    // Detailed items sheet
    const itemRows: any[] = [];
    submittedExpenses.forEach((expense) => {
      const ai = (expense as any).ApprovedItem || (expense as any).approvedItem || {};
      const ar = ai.aproval_request || {};
      (expense.expenseItems || []).forEach((item: any) => {
        const cost = item.total !== undefined ? item.total : (item.rate || 0) * (item.quantity || 0);
        itemRows.push({
          'Expense Title': expense.title || '',
          'Date': expense.createdAt
            ? new Date(expense.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '',
          'Category': ar.title || '',
          'Item': item.item || '',
          'Quantity': item.quantity ?? '',
          'Rate (₹)': item.rate ?? '',
          'Total (₹)': cost,
        });
      });
    });
    if (itemRows.length > 0) {
      const itemsWs = XLSX.utils.json_to_sheet(itemRows);
      XLSX.utils.book_append_sheet(wb, itemsWs, 'Expense Line Items');
    }

    const timestamp = new Date().toLocaleString('en-IN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).replace(/[/:,\s]/g, '-');
    XLSX.writeFile(wb, `Expenses_${timestamp}.xlsx`);
  } catch (err) {
    console.error('Error downloading spreadsheet:', err);
    alert('Failed to download spreadsheet. Please try again.');
  }
};

/**
 * Styled Individual Expenses spreadsheet with cycling category colors and a total row.
 */
export const downloadIndividualExpensesSpreadsheet = (submittedExpenses: ExpenseItem[]) => {
  try {
    const XLSX = require('xlsx-js-style');
    const wb = XLSX.utils.book_new();

    const groupPalettes = [
      { headerBg: 'D6E4F0', itemBg: 'E3F2FD', border: '1976D2' },
      { headerBg: 'D5ECD5', itemBg: 'E8F5E9', border: '2E7D32' },
      { headerBg: 'E8D5F0', itemBg: 'F3E5F5', border: '7B1FA2' },
      { headerBg: 'FDDCB5', itemBg: 'FFF3E0', border: 'E65100' },
      { headerBg: 'C5ECF0', itemBg: 'E0F7FA', border: '00838F' },
    ];

    const solidBorder = (color: string) => ({ style: 'thin' as const, color: { rgb: color } });

    // ── Build row data ──
    const rows = submittedExpenses.map((expense, idx) => {
      const ai = (expense as any).ApprovedItem || (expense as any).approvedItem || {};
      const ie = ai.item_estimate || {};
      const ar = ai.aproval_request || {};
      const spent = getExpenseTotalAmount(expense);
      const exStatus = (expense as any).ExceptionApproval?.status || '';
      const approvedBudget =
        expense.isExceptionRequest && exStatus === 'Approved'
          ? spent
          : expense.isExceptionRequest && exStatus === 'Rejected'
          ? 0
          : ai.totalPrice || 0;
      const remaining = ai.remianingAmount ?? ai.remainingAmount ?? 0;
      const category = ar.title || ie.item || (expense.isExceptionRequest ? 'Exception' : '-');
      return {
        slno: idx + 1,
        title: expense.title || '(Untitled)',
        date: expense.createdAt
          ? new Date(expense.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : '',
        category,
        approvedBudget,
        spent,
        remaining,
        itemCount: expense.expenseItems?.length || 0,
        itemsList: (expense.expenseItems || []).map((it: any) => it.item || '').filter(Boolean).join(', ') || 'No items',
        exception: expense.isExceptionRequest ? 'Yes' : 'No',
        exStatus: exStatus || (expense.isExceptionRequest ? 'Pending' : ''),
      };
    });

    // Assign palette per row index (cycling)
    let rowIdx = 0;

    const COL_COUNT = 11;
    const headers = ['#', 'Title', 'Date', 'Category', 'Approved Budget (₹)', 'Spent (₹)', 'Remaining (₹)', 'Item Count', 'Items List', 'Exception', 'Approval Status'];
    const ws: any = {};
    let row = 0;

    // Header row
    headers.forEach((h, c) => {
      ws[XLSX.utils.encode_cell({ r: row, c })] = {
        v: h, t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          fill: { fgColor: { rgb: '333333' } },
          alignment: { horizontal: (c >= 4 && c <= 7) ? 'right' : 'left', vertical: 'center' },
          border: { top: solidBorder('333333'), bottom: solidBorder('333333'), left: solidBorder('333333'), right: solidBorder('333333') },
        },
      };
    });
    row++;

    let grandApproved = 0, grandSpent = 0, grandRemaining = 0;

    rows.forEach((r) => {
      const palette = groupPalettes[rowIdx % groupPalettes.length];
      const bc = palette.border;
      rowIdx++;
      grandApproved += r.approvedBudget;
      grandSpent += r.spent;
      grandRemaining += r.remaining;

      const values: { v: any; t: 's' | 'n' }[] = [
        { v: r.slno, t: 'n' },
        { v: r.title, t: 's' },
        { v: r.date, t: 's' },
        { v: r.category, t: 's' },
        { v: r.approvedBudget, t: 'n' },
        { v: r.spent, t: 'n' },
        { v: r.remaining, t: 'n' },
        { v: r.itemCount, t: 'n' },
        { v: r.itemsList, t: 's' },
        { v: r.exception, t: 's' },
        { v: r.exStatus, t: 's' },
      ];

      values.forEach((cell, c) => {
        ws[XLSX.utils.encode_cell({ r: row, c })] = {
          v: cell.v, t: cell.t,
          s: {
            font: { sz: 10, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: palette.itemBg } },
            alignment: { horizontal: (c >= 4 && c <= 7) ? 'right' : 'left', vertical: 'center', wrapText: c === 8 },
            border: {
              left: c === 0 ? solidBorder(bc) : undefined,
              right: c === COL_COUNT - 1 ? solidBorder(bc) : undefined,
              bottom: solidBorder('E0E0E0'),
            },
          },
        };
        if (cell.t === 'n' && c >= 4 && c <= 6) {
          ws[XLSX.utils.encode_cell({ r: row, c })].z = '#,##0.00';
        }
      });
      row++;
    });

    // ── Total row ──
    const totalValues: { v: any; t: 's' | 'n' }[] = [
      { v: '', t: 's' }, { v: 'TOTAL', t: 's' }, { v: '', t: 's' }, { v: '', t: 's' },
      { v: grandApproved, t: 'n' }, { v: grandSpent, t: 'n' }, { v: grandRemaining, t: 'n' },
      { v: '', t: 's' }, { v: '', t: 's' }, { v: '', t: 's' }, { v: '', t: 's' },
    ];
    totalValues.forEach((cell, c) => {
      ws[XLSX.utils.encode_cell({ r: row, c })] = {
        v: cell.v, t: cell.t,
        s: {
          font: { bold: true, sz: 11, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'D9D9D9' } },
          alignment: { horizontal: (c >= 4 && c <= 6) ? 'right' : 'left', vertical: 'center' },
          border: {
            top: { style: 'medium' as const, color: { rgb: '333333' } },
            bottom: { style: 'medium' as const, color: { rgb: '333333' } },
            left: c === 0 ? { style: 'medium' as const, color: { rgb: '333333' } } : undefined,
            right: c === COL_COUNT - 1 ? { style: 'medium' as const, color: { rgb: '333333' } } : undefined,
          },
        },
      };
      if (cell.t === 'n') {
        ws[XLSX.utils.encode_cell({ r: row, c })].z = '#,##0.00';
      }
    });
    row++;

    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row - 1, c: COL_COUNT - 1 } });
    ws['!cols'] = [
      { wch: 5 }, { wch: 28 }, { wch: 14 }, { wch: 22 },
      { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 10 },
      { wch: 40 }, { wch: 10 }, { wch: 16 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Individual Expenses');

    const timestamp = new Date().toLocaleString('en-IN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).replace(/[/:,\s]/g, '-');
    XLSX.writeFile(wb, `Expenses_Individual_${timestamp}.xlsx`);
  } catch (err) {
    console.error('Error downloading Individual Expenses spreadsheet:', err);
    alert('Failed to download spreadsheet. Please try again.');
  }
};
